/**
 * Thin wrappers over the host's axios (its interceptor handles 401/403/419).
 *
 * `request()` follows the house rule: `response.data` on success, `null` on
 * any failure. `settled()` is for the actions whose error code the UI has
 * to map (a 409 `version_frozen`, a 422 `missing_fields`, a 403
 * `service_unavailable`): it opts out of the global 403 redirect with
 * `settleAuthErrors` and resolves to `{ status, data }` whatever happens.
 */
import axios from "@/axios";
import authHeader from "@/services/auth-header";
import { config } from "@/config";

export const BASE = `${config["apiUrl"]}quotes`;

export function url(path = "") {
  return path ? `${BASE}/${path}` : BASE;
}

function log(error) {
  console.log("ERROR:: ", error.response ? error.response.data : error.message);
}

export function request(method, path, { params, data, responseType } = {}) {
  return axios({ method, url: url(path), params, data, responseType, headers: authHeader() })
    .then((response) => (response.status >= 200 && response.status < 300 ? response.data : null))
    .catch((error) => {
      log(error);
      return null;
    });
}

/**
 * A file download: `{ blob, filename }` with the name the API sends in
 * Content-Disposition (the proposal's name), or null on failure.
 */
export function download(method, path, { params, data } = {}) {
  return axios({ method, url: url(path), params, data, responseType: "blob", headers: authHeader() })
    .then((response) => ({
      blob: response.data,
      filename: filenameOf(response.headers["content-disposition"]),
      type: response.headers["content-type"],
    }))
    .catch((error) => {
      log(error);
      return null;
    });
}

/** The UTF-8 name of an RFC 5987 Content-Disposition, else the plain one, else null. */
export function filenameOf(disposition) {
  const text = String(disposition || "");
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(text);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch (e) {
      return utf8[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(text);
  return plain ? plain[1] : null;
}

/** A multipart upload (`FormData`), settled like the other actions. */
export function upload(path, formData) {
  return axios({
    method: "post",
    url: url(path),
    data: formData,
    headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
    settleAuthErrors: true,
  })
    .then((response) => ({ status: response.status, data: response.data }))
    .catch((error) => {
      log(error);
      if (error.response) {
        return { status: error.response.status, data: error.response.data || {} };
      }
      return { status: 0, data: { message: error.message, code: "network" } };
    });
}

export function settled(method, path, { params, data } = {}) {
  return axios({
    method,
    url: url(path),
    params,
    data,
    headers: authHeader(),
    settleAuthErrors: true,
  })
    .then((response) => ({ status: response.status, data: response.data }))
    .catch((error) => {
      log(error);
      if (error.response) {
        return { status: error.response.status, data: error.response.data || {} };
      }
      return { status: 0, data: { message: error.message, code: "network" } };
    });
}
